import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../api/client.js';

/**
 * Auth state is one of:
 *   { status: 'loading',       user: null }
 *   { status: 'authenticated', user: { email, name, picture, role } }
 *   { status: 'anonymous',     user: null }
 *
 * Network errors while fetching /api/auth/me are treated as 'anonymous' —
 * the app renders the login page and the user can retry from there. This
 * keeps the happy-path code simple (no separate error state to handle).
 */
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null });

  const refresh = useCallback(async () => {
    try {
      const data = await api('/api/auth/me');
      if (data?.authenticated) {
        setState({
          status: 'authenticated',
          user: {
            email: data.email,
            name: data.name,
            picture: data.picture,
            role: data.role ?? null, // §8a — drives per-role nav/gating in 8c/8d
            permissions: data.permissions ?? null, // §8c — { all, features } (resource levels are enforced server-side)
            eachangeEnabled: data.eachange_enabled === true, // §8d — gate link visibility
            changeEnabled: data.change_enabled === true, // §9 — gate link visibility
            sponsorsEnabled: data.sponsors_enabled === true, // sponsor-audit — gate link visibility
            deploypanelEnabled: data.deploypanel_enabled === true, // deploypanel — gate link visibility
          },
        });
      } else {
        setState({ status: 'anonymous', user: null });
      }
    } catch {
      setState({ status: 'anonymous', user: null });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setState({ status: 'anonymous', user: null });
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ ...state, refresh, logout }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
